import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  FunnelIcon,
  EyeSlashIcon,
  ArrowDownTrayIcon,
  ViewColumnsIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline'
import Button from '@/components/ui/Button'
import { Badge } from '@/components/catalyst/badge'
import FormCheckbox from '@/components/ui/form/FormCheckbox'

interface ToolbarProps {
  // Global filters
  excludeShipped: boolean
  onExcludeShippedChange: (v: boolean) => void
  excludeClosedLost: boolean
  onExcludeClosedLostChange: (v: boolean) => void
  showShippedLast90: boolean
  onShowShippedLast90Change: (v: boolean) => void
  excludeParentClosedGmv: boolean
  onExcludeParentClosedGmvChange: (v: boolean) => void
  // Actions
  onClearSettings: () => void
  onClearFilters: () => void
  onOpenPresets: () => void
  onExportExcel: (mode: 'all' | 'selected') => void
  onOpenColumnChooser: () => void
  // Fullscreen
  fullscreen: boolean
  onToggleFullscreen: () => void
  // Data
  rowCount: number
  selectedCount: number
  // Show in VPO action — opens /vpo/new with the selected order IDs as
  // a query param. Only enabled while the user has rows selected.
  onShowInVpo?: () => void
  canShowInVpo?: boolean
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

export default function Toolbar({
  excludeShipped,
  onExcludeShippedChange,
  excludeClosedLost,
  onExcludeClosedLostChange,
  showShippedLast90,
  onShowShippedLast90Change,
  excludeParentClosedGmv,
  onExcludeParentClosedGmvChange,
  onClearSettings,
  onClearFilters,
  onOpenPresets,
  onExportExcel,
  onOpenColumnChooser,
  fullscreen,
  onToggleFullscreen,
  rowCount,
  selectedCount,
  onShowInVpo,
  canShowInVpo,
}: ToolbarProps) {
  const activeFilterCount = [excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv].filter(
    Boolean
  ).length

  return (
    <div data-testid="dashboard-toolbar" className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900">
      {/* Left side: counts + selection-only actions */}
      <div className="flex items-center gap-2">
        <Badge color="zinc">
          <span data-testid="orders-count" className="text-xs font-medium">{formatNumber(rowCount)} orders</span>
        </Badge>
        {selectedCount > 0 && (
          <Badge color="indigo">
            <span data-testid="selected-count" className="text-xs font-medium">{formatNumber(selectedCount)} selected</span>
          </Badge>
        )}
        {/* Show In VPO — only surfaces when the user has rows selected,
            sits next to the "N selected" badge so the action lives where
            the user is looking. Hidden entirely (not greyed out) when
            nothing is selected — there's no useful disabled state. */}
        {canShowInVpo && selectedCount > 0 && (
          <Button
            data-testid="toolbar-show-in-vpo"
            title="Open selected orders in a new VPO draft"
            aria-label="Show selected orders in VPO"
            variant="primary"
            size="xs"
            className="inline-flex h-7 items-center justify-center gap-1.5 whitespace-nowrap"
            onClick={onShowInVpo}
          >
            Show In VPO
            <span className="ml-0.5 rounded bg-white/20 px-1 text-[10px]">{selectedCount}</span>
          </Button>
        )}
      </div>

      {/* Right side: buttons */}
      <div className="flex items-center gap-1.5">
        {/* Fullscreen */}
        <Button data-testid="toolbar-fullscreen" title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap" onClick={onToggleFullscreen}>
          {fullscreen
            ? <ArrowsPointingInIcon className="h-3.5 w-3.5 shrink-0" />
            : <ArrowsPointingOutIcon className="h-3.5 w-3.5 shrink-0" />
          }
          <span className="hidden xl:inline">{fullscreen ? 'Exit' : 'Fullscreen'}</span>
        </Button>

        {/* Separator */}
        <div className="mx-1 hidden h-5 w-px bg-zinc-200 xl:block dark:bg-zinc-700" />

        {/* Presets */}
        <Button data-testid="toolbar-presets" title="Filter presets" aria-label="Filter presets" variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap" onClick={onOpenPresets}>
          <FunnelIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden xl:inline">Presets</span>
        </Button>

        {/* Hide dropdown (Popover to stay open on checkbox click) */}
        <Popover className="relative">
          <PopoverButton data-testid="toolbar-hide" title="Hide rows by state" aria-label="Hide rows by state" as={Button} variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap">
            <EyeSlashIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="hidden xl:inline">Hide</span>
            {activeFilterCount > 0 && (
              <span data-testid="active-filter-count" className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-medium text-white">
                {activeFilterCount}
              </span>
            )}
          </PopoverButton>

          <PopoverPanel
            data-testid="toolbar-hide-panel"
            anchor="bottom end"
            transition
            className="z-50 mt-1 w-64 rounded-xl border border-zinc-200 bg-white/75 p-1 shadow-lg backdrop-blur-xl transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 dark:border-zinc-700 dark:bg-zinc-800/75"
          >
            <div className="space-y-0.5">
              <div data-testid="filter-exclude-shipped" onClick={() => onExcludeShippedChange(!excludeShipped)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5">
                <span onClick={e => e.stopPropagation()}><FormCheckbox checked={excludeShipped} onChange={onExcludeShippedChange} /></span>
                Hide Shipped To Client
              </div>
              <div data-testid="filter-exclude-closed-lost" onClick={() => onExcludeClosedLostChange(!excludeClosedLost)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5">
                <span onClick={e => e.stopPropagation()}><FormCheckbox checked={excludeClosedLost} onChange={onExcludeClosedLostChange} /></span>
                Hide Closed/Lost
              </div>
              <div data-testid="filter-shipped-last-90" onClick={() => onShowShippedLast90Change(!showShippedLast90)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5">
                <span onClick={e => e.stopPropagation()}><FormCheckbox checked={showShippedLast90} onChange={onShowShippedLast90Change} /></span>
                Show Shipped Last 90 Days
              </div>
              <div data-testid="filter-exclude-parent-close-gmv" onClick={() => onExcludeParentClosedGmvChange(!excludeParentClosedGmv)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5">
                <span onClick={e => e.stopPropagation()}><FormCheckbox checked={excludeParentClosedGmv} onChange={onExcludeParentClosedGmvChange} /></span>
                Hide Parent Closed GMV
              </div>
            </div>
          </PopoverPanel>
        </Popover>

        {/* Export dropdown */}
        <Popover className="relative">
          <PopoverButton data-testid="toolbar-export" title="Export to Excel" aria-label="Export to Excel" as={Button} variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap">
            <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="hidden xl:inline">Export</span>
          </PopoverButton>

          <PopoverPanel
            data-testid="toolbar-export-panel"
            anchor="bottom end"
            transition
            className="z-50 mt-1 w-48 rounded-xl border border-zinc-200 bg-white/75 p-1 shadow-lg backdrop-blur-xl transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 dark:border-zinc-700 dark:bg-zinc-800/75"
          >
            {({ close }: { close: () => void }) => (
              <div className="space-y-0.5">
                <button
                  data-testid="export-all"
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5"
                  onClick={() => { onExportExcel('all'); close() }}
                >
                  Export All
                </button>
                <button
                  data-testid="export-selected"
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-950 hover:bg-zinc-950/5 disabled:cursor-not-allowed disabled:opacity-40 dark:text-white dark:hover:bg-white/5"
                  disabled={selectedCount === 0}
                  onClick={() => { onExportExcel('selected'); close() }}
                >
                  Export Selected ({formatNumber(selectedCount)})
                </button>
              </div>
            )}
          </PopoverPanel>
        </Popover>

        {/* Columns */}
        <Button data-testid="toolbar-columns" title="Show/hide columns" aria-label="Show/hide columns" variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap" onClick={onOpenColumnChooser}>
          <ViewColumnsIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden xl:inline">Columns</span>
        </Button>


        {/* Clear Filters */}
        <Button data-testid="toolbar-clear-filters" title="Clear all column filters" aria-label="Clear all column filters" variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap" onClick={onClearFilters}>
          <XMarkIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden xl:inline">Clear Filters</span>
        </Button>

        {/* Reset */}
        <Button data-testid="toolbar-reset" title="Reset toolbar settings" aria-label="Reset toolbar settings" variant="secondary" size="xs" className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 whitespace-nowrap" onClick={onClearSettings}>
          <ArrowPathIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden xl:inline">Reset</span>
        </Button>
      </div>
    </div>
  )
}
